import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { unitTypeService, UnitType } from '../../services/configurationService';
import { customToast } from '../../utils/customToast';
import { Plus, Edit, Trash2 } from 'lucide-react';
import UnitsModal from './UnitsModal';
import ConfirmationModal from '../ConfirmationModal';

const UnitsManagement: React.FC = () => {
  const { theme } = useTheme();
  const [units, setUnits] = useState<UnitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState<UnitType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<UnitType | null>(null);

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    try {
      setLoading(true);
      const response = await unitTypeService.getAll();
      setUnits(Array.isArray(response) ? response : (response as any).data || []);
    } catch (error: any) {
      customToast.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setSelectedUnit(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (unit: UnitType) => {
    setSelectedUnit(unit);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (unit: UnitType) => {
    setUnitToDelete(unit);
  };

  const handleConfirmDelete = async () => {
    if (!unitToDelete) return;

    try {
      await unitTypeService.delete(unitToDelete.id);
      customToast.success('Unit deleted successfully');
      loadUnits();
    } catch (error: any) {
      customToast.error(error.response?.data?.message || 'Failed to delete unit');
    } finally {
      setUnitToDelete(null);
    }
  };

  const handleModalSave = () => {
    setIsModalOpen(false);
    loadUnits();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h2 className={`text-lg sm:text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Unit Types Management
          </h2>
          <p className={`text-xs sm:text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage measurement units for your products
          </p>
        </div>
        <button
          onClick={handleAddClick}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm font-medium touch-manipulation"
        >
          <Plus size={18} />
          <span>Add Unit</span>
        </button>
      </div>

      {/* Units Table */}
      {loading ? (() => {
        const skeletonColor = theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300';
        return (
          <div className={`rounded-lg border ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className="px-4 py-3">
                      <div className={`h-4 w-20 rounded animate-pulse ${skeletonColor}`} />
                    </th>
                    <th className="px-4 py-3">
                      <div className={`h-4 w-24 rounded animate-pulse ${skeletonColor}`} />
                    </th>
                    <th className="px-4 py-3">
                      <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor}`} />
                    </th>
                    <th className="px-4 py-3">
                      <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor}`} />
                    </th>
                    <th className="px-4 py-3 text-right">
                      <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor} ml-auto`} />
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <tr key={idx} className={theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                      {/* Column 1: Unit Name */}
                      <td className="px-4 py-3">
                        <div className={`h-4 w-24 rounded animate-pulse ${skeletonColor}`} />
                      </td>

                      {/* Column 2: Short Name */}
                      <td className="px-4 py-3">
                        <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor}`} />
                      </td>

                      {/* Column 3: Type */}
                      <td className="px-4 py-3">
                        <div className={`h-4 w-20 rounded animate-pulse ${skeletonColor}`} />
                      </td>

                      {/* Column 4: Status Badge (rounded-full) */}
                      <td className="px-4 py-3">
                        <div className={`h-6 w-24 rounded-full animate-pulse ${skeletonColor}`} />
                      </td>

                      {/* Column 5: Action Icons (Edit + Delete icons only) */}
                      <td className="px-4 py-3">
                        <div className="flex justify-end space-x-2">
                          {/* Edit Icon */}
                          <div className={`w-6 h-6 rounded animate-pulse ${skeletonColor}`} />
                          {/* Delete Icon */}
                          <div className={`w-6 h-6 rounded animate-pulse ${skeletonColor}`} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })() : units.length === 0 ? (
        <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          No units found. Click "Add Unit" to create one.
        </div>
      ) : (
        <div className={`overflow-x-auto rounded-lg border ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <table className="w-full">
            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Unit Name
                </th>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Short Name
                </th>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Type
                </th>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Status
                </th>
                <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {units.map((unit) => (
                <tr key={unit.id} className={theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                  <td className={`px-4 py-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {unit.name}
                  </td>
                  <td className={`px-4 py-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {unit.short_name}
                  </td>
                  <td className={`px-4 py-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="capitalize">{unit.type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      unit.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {unit.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEditClick(unit)}
                        className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(unit)}
                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {isModalOpen && (
        <UnitsModal
          unit={selectedUnit}
          onClose={() => setIsModalOpen(false)}
          onSave={handleModalSave}
        />
      )}

      {unitToDelete && (
        <ConfirmationModal
          isOpen={true}
          title="Delete Unit"
          message={
            <>
              Are you sure you want to delete the unit <strong>{unitToDelete.name}</strong>?
              <br />
              This action cannot be undone.
            </>
          }
          onConfirm={handleConfirmDelete}
          onClose={() => setUnitToDelete(null)}
          confirmText="Delete"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
    </div>
  );
};

export default UnitsManagement;
